import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { TableProps } from 'antd';
import {
  CodeOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  ProfileOutlined,
  SaveOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { Bento, DataTable, PageHeader, TabsTruot, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty, Field, Select, Skeleton, Textarea } from '../../../components/ui';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useT } from '../../../i18n';
import { ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { layTuChoi } from '../../../lib/repositories/tro-ly-ai.repository';
import { useToast } from '../../../lib/toast';
import { DaiLoi } from '../components/chung/VungDuLieu';
import { useDanhSachNguon } from '../hooks/useChung';
import { useMoTaLoi } from '../hooks/useMoTaLoi';
import { useChayCauLenh, useGoiNguCanh, useThemCauMau } from '../hooks/useVanHanh';
import { daVietLai } from '../nghiep-vu/van-hanh';
import type { QueryResult, Refusal } from '../types';

/** Số dòng kết quả vẽ ra màn — đủ để kiểm, không biến bàn thử thành trình xuất dữ liệu. */
const SO_DONG_HIEN = 100;

/** Câu SQL mẫu trong ô trống — mã SQL, không phải chữ giao diện. */
const SQL_MAU = 'SELECT khu_vuc, SUM(san_luong_that_thoat)\nFROM that_thoat\nGROUP BY khu_vuc';

type Dong = Record<string, unknown> & { __k: number };

function KhoiTuChoi({ ds }: { ds: Refusal[] }) {
  const { t } = useT();
  return (
    <div role="alert" className="space-y-2 rounded-lg border border-warning-500 bg-warning-50 p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium text-warning-700">
        <WarningOutlined aria-hidden /> {t('troLyAiVanHanhThuNghiem.congTuChoi')}
      </p>
      {ds.map((r, i) => (
        <p key={i} className="text-xs leading-relaxed text-warning-700">
          <span className="me-1 font-mono text-xs">{r.code}</span>
          {r.message}
        </p>
      ))}
    </div>
  );
}

function BangKetQua({ kq }: { kq: QueryResult }) {
  const { t } = useT();
  const cot: TableProps<Dong>['columns'] = kq.columns.map((c) => ({
    title: c,
    dataIndex: c,
    key: c,
    render: (v: unknown) =>
      v === null || v === undefined ? (
        <span className="text-content-muted">null</span>
      ) : (
        <span className="whitespace-nowrap tabular-nums text-content-secondary">{String(v)}</span>
      ),
  }));
  const dong = kq.rows.slice(0, SO_DONG_HIEN).map((r, i) => ({ ...r, __k: i }));
  return (
    <DataTable<Dong>
      caption={t('troLyAiVanHanhThuNghiem.bangKetQua')}
      rowKey="__k"
      size="small"
      columns={cot}
      dataSource={dong}
      pagination={false}
    />
  );
}

/**
 * Bàn thử của luồng bản sao (`/tro-ly-ai/thu-nghiem`, port `PlaygroundView` native). Trả lời hai câu, tách rời nhau:
 *
 * 1. *Mô hình nhìn thấy gì?* — gói ngữ cảnh, nguyên văn chuỗi đi vào lời nhắc. Khi trợ lý trả lời sai, câu đầu tiên phải
 *    hỏi là nó đã được kể những gì.
 * 2. *Câu lệnh này có được phép không?* — chạy thẳng qua bộ canh cổng, KHÔNG có mô hình nào tham gia: chỗ duy nhất tách
 *    được "luật chặn sai" khỏi "mô hình viết sai".
 *
 * In cả câu ĐÃ VIẾT LẠI: bộ canh cổng chèn trần dòng, gắn tên lược đồ, chuyển phương ngữ — in lại câu người ta gõ là in
 * một câu KHÁC với câu đã sinh ra những con số bên dưới.
 */
export function PlaygroundView() {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const duongDan = useDuongDan();
  const [chonNguon, setChonNguon] = useState('');
  const [sql, setSql] = useState('');
  const [cauHoi, setCauHoi] = useState('');
  const [ketQua, setKetQua] = useState<QueryResult | null>(null);
  const [tuChoi, setTuChoi] = useState<Refusal[]>([]);
  const [loiChay, setLoiChay] = useState<unknown>(null);

  const nguon = useDanhSachNguon();
  const sanSang = useMemo(
    () => (nguon.data ?? []).filter((s) => s.status === 'ready' || s.status === 'connected'),
    [nguon.data],
  );
  // Chưa chọn thì lấy nguồn sẵn sàng đầu tiên.
  const sourceId = chonNguon || sanSang[0]?.id || '';
  const ctx = useGoiNguCanh(sourceId, cauHoi.trim());
  const chay = useChayCauLenh();
  const luuMau = useThemCauMau();

  const onChay = () => {
    setKetQua(null);
    setTuChoi([]);
    setLoiChay(null);
    chay.mutate(
      { sourceId, sql, question: cauHoi.trim() || undefined },
      {
        onSuccess: setKetQua,
        // Từ chối của bộ canh cổng KHÔNG phải lỗi hệ thống — nó là câu trả lời, mỗi dòng một chỗ sửa được.
        onError: (e) => {
          const r = layTuChoi(e);
          if (r.length) setTuChoi(r);
          else setLoiChay(e);
        },
      },
    );
  };

  /**
   * Lưu câu vừa chạy được thành CÂU MẪU — nấc mạnh nhất trong ba nấc tín hiệu (thuật ngữ, định nghĩa, câu mẫu). Nút ở
   * đây vì đây là chỗ người ta VỪA dò ra câu đúng. Gửi câu NGƯỜI DÙNG GÕ, không gửi `ketQua.sql`: máy chủ chạy lại qua
   * bộ canh cổng rồi mới lưu, gửi bản đã viết lại là viết lại HAI lần.
   */
  const onLuuMau = () =>
    luuMau.mutate(
      { sourceId, question: cauHoi.trim(), sql, generatedSql: ketQua?.sql ?? null },
      {
        onSuccess: () => toast.success(t('troLyAiVanHanhThuNghiem.daLuuMau')),
        onError: (e) => toast.error(moTaLoi(e)),
      },
    );

  const coVietLai = ketQua !== null && daVietLai(sql, ketQua.sql);

  const oCauHoi = (nhan: string, goiY: string) => (
    <Field label={nhan}>
      {(p) => (
        <Textarea
          {...p}
          autoSize={{ minRows: 1, maxRows: 4 }}
          value={cauHoi}
          onChange={(e) => setCauHoi(e.target.value)}
          placeholder={goiY}
        />
      )}
    </Field>
  );

  const theCauLenh = (
    <Bento>
      <Tile span="lg:col-span-5">
        <TileHeader title={t('troLyAiVanHanhThuNghiem.tabCauLenh')} icon={<CodeOutlined />} tone="primary" />
        <TileBody className="space-y-3">
          {oCauHoi(t('troLyAiVanHanhThuNghiem.cauHoiTuyChon'), t('troLyAiVanHanhThuNghiem.viDuCauHoi'))}
          <Field label={t('troLyAiVanHanhThuNghiem.cauLenhSql')}>
            {(p) => (
              <Textarea
                {...p}
                className="font-mono text-xs"
                autoSize={{ minRows: 8, maxRows: 20 }}
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                placeholder={SQL_MAU}
              />
            )}
          </Field>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-content-muted">
              {ctx.data
                ? t('troLyAiVanHanhThuNghiem.tranDong', { dialect: ctx.data.dialect, n: soVN(ctx.data.maxRows, locale) })
                : ''}
            </p>
            <Button
              size="sm"
              variant="primary"
              icon={<PlayCircleOutlined />}
              loading={chay.isPending}
              disabled={!sourceId || !sql.trim()}
              onClick={onChay}
            >
              {t('troLyAiVanHanhThuNghiem.chayQuaCong')}
            </Button>
          </div>

          {ctx.data?.samples?.length ? (
            <div className="space-y-1 border-t border-default pt-3">
              <p className="text-xs font-medium text-content-secondary">{t('troLyAiVanHanhThuNghiem.cauMauDaLuu')}</p>
              {ctx.data.samples.slice(0, 5).map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className="block w-full truncate rounded px-1 py-0.5 text-start text-xs text-content-secondary hover:bg-subtle"
                  onClick={() => {
                    setCauHoi(s.question);
                    setSql(s.sql);
                  }}
                >
                  {s.question}
                </button>
              ))}
            </div>
          ) : null}
        </TileBody>
      </Tile>

      <Tile span="lg:col-span-7">
        <TileHeader title={t('troLyAiVanHanhThuNghiem.ketQua')} icon={<ProfileOutlined />} tone="teal" />
        <TileBody className="space-y-3">
          {tuChoi.length ? <KhoiTuChoi ds={tuChoi} /> : null}
          {loiChay ? <DaiLoi loi={loiChay} /> : null}

          {chay.isPending ? (
            <div className="space-y-2" aria-busy="true">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : ketQua ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="success">{t('troLyAiVanHanhThuNghiem.soDong', { n: soVN(ketQua.rowCount, locale) })}</Badge>
                <Badge variant="neutral">{soVN(ketQua.elapsedMs, locale)} ms</Badge>
                {ketQua.truncated ? <Badge variant="warning">{t('troLyAiVanHanhThuNghiem.daCatTran')}</Badge> : null}
                {ketQua.freshness === 'live' ? (
                  <span className="text-xs text-content-muted">{t('troLyAiVanHanhThuNghiem.soLieuTrucTiep')}</span>
                ) : ketQua.syncedAt ? (
                  <span className="text-xs text-content-muted">
                    {t('troLyAiVanHanhThuNghiem.duLieuToi', { luc: ngayGioVN(ketQua.syncedAt, locale) })}
                  </span>
                ) : null}
                {/* Đòi CÂU HỎI, không chỉ câu lệnh: câu mẫu được ghép vào ngữ cảnh theo độ giống với câu người dùng hỏi
                    — câu mẫu không có câu hỏi thì không bao giờ được chọn, lưu xong mà không dùng được. */}
                <span className="ms-auto flex flex-wrap items-center gap-2">
                  {!cauHoi.trim() ? (
                    <span className="text-xs text-content-muted">{t('troLyAiVanHanhThuNghiem.mauCanCauHoi')}</span>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<SaveOutlined />}
                    loading={luuMau.isPending}
                    disabled={!cauHoi.trim() || !sql.trim()}
                    onClick={onLuuMau}
                  >
                    {t('troLyAiVanHanhThuNghiem.luuLamMau')}
                  </Button>
                </span>
              </div>

              {/* Cảnh báo do máy chủ soạn — không dịch. */}
              {ketQua.warnings?.map((w, i) => (
                <p key={i} className="text-xs text-warning-700">
                  {w}
                </p>
              ))}

              {coVietLai ? (
                <div>
                  <p className="mb-1 text-xs font-medium text-content-secondary">
                    {t('troLyAiVanHanhThuNghiem.cauDaVietLai')}
                  </p>
                  <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg border border-default bg-subtle p-3 font-mono text-xs text-content-primary">
                    {ketQua.sql}
                  </pre>
                </div>
              ) : null}

              <BangKetQua kq={ketQua} />
              {ketQua.rows.length > SO_DONG_HIEN ? (
                <p className="text-xs text-content-muted">
                  {t('troLyAiVanHanhThuNghiem.hien100', { n: soVN(ketQua.rowCount, locale) })}
                </p>
              ) : null}
            </>
          ) : !tuChoi.length && !loiChay ? (
            <p className="py-12 text-center text-xs text-content-muted">{t('troLyAiVanHanhThuNghiem.ketQuaHienODay')}</p>
          ) : null}
        </TileBody>
      </Tile>
    </Bento>
  );

  const theNguCanh = (
    <Bento>
      <Tile span="lg:col-span-4">
        <TileHeader title={t('troLyAiVanHanhThuNghiem.tomTatGoi')} icon={<FileTextOutlined />} tone="violet" />
        <TileBody className="space-y-3">
          {oCauHoi(t('troLyAiVanHanhThuNghiem.cauHoiCatNguCanh'), t('troLyAiVanHanhThuNghiem.deTrongXemToanBo'))}
          {ctx.data ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="neutral">{ctx.data.dialect}</Badge>
              <Badge variant="neutral">{t('troLyAiVanHanhThuNghiem.soBang', { n: soVN(ctx.data.tables.length, locale) })}</Badge>
              <Badge variant="neutral">{t('troLyAiVanHanhThuNghiem.soLuat', { n: soVN(ctx.data.rules.length, locale) })}</Badge>
              <Badge variant="neutral">
                {t('troLyAiVanHanhThuNghiem.soThuatNgu', { n: soVN(ctx.data.glossary.length, locale) })}
              </Badge>
              <Badge variant="neutral">{t('troLyAiVanHanhThuNghiem.soCauMau', { n: soVN(ctx.data.samples.length, locale) })}</Badge>
              <span className="text-xs text-content-muted">
                {t('troLyAiVanHanhThuNghiem.soKyTu', { n: soVN(ctx.data.promptText.length, locale) })}
              </span>
            </div>
          ) : null}
        </TileBody>
      </Tile>

      <Tile span="lg:col-span-8" aria-label={t('troLyAiVanHanhThuNghiem.noiDungGoi')}>
        {ctx.isPending && !!sourceId ? (
          <div className="space-y-2 p-4" aria-busy="true">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : ctx.error ? (
          <DaiLoi loi={ctx.error} onThuLai={() => void ctx.refetch()} />
        ) : (
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap p-4 font-mono text-xs leading-relaxed text-content-secondary">
            {ctx.data?.promptText ?? ''}
          </pre>
        )}
      </Tile>
    </Bento>
  );

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.thuNghiem') }]}
          titleIcon={<ExperimentOutlined className="text-primary-600" />}
          title={t('troLyAiVanHanhThuNghiem.tieuDe')}
          subtitle={t('troLyAiVanHanhThuNghiem.phuDe')}
          actions={
            <Select<string>
              aria-label={t('troLyAiVanHanhThuNghiem.chonNguon')}
              placeholder={t('troLyAiVanHanhThuNghiem.chonNguon')}
              className="w-56"
              value={sourceId || undefined}
              onChange={(v) => setChonNguon(v)}
              loading={nguon.isPending}
              options={sanSang.map((s) => ({ value: s.id, label: s.name }))}
            />
          }
        />

        {/* Lỗi gọi API vẽ RIÊNG khỏi trạng thái rỗng: gộp lại thì một lần 403 đọc y hệt "chưa khai nguồn nào". */}
        {nguon.isError ? (
          <div className="rounded-lg border border-danger-100 bg-danger-50 p-3 text-xs text-danger-700" role="alert">
            <p className="font-medium">{t('troLyAiVanHanhThuNghiem.loiNguonTieuDe')}</p>
            <p className="mt-1 leading-relaxed">{moTaLoi(nguon.error)}</p>
            <p className="mt-1 leading-relaxed text-content-secondary">{t('troLyAiVanHanhThuNghiem.loiNguonGoiY')}</p>
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => void nguon.refetch()}>
              {t('chung.thuLai')}
            </Button>
          </div>
        ) : !nguon.isPending && !sanSang.length ? (
          <div className="rounded-lg border border-default bg-surface">
            <Empty
              title={t('troLyAiVanHanhThuNghiem.chuaCoNguonSanSang')}
              action={
                <Link to={duongDan('/tro-ly-ai/ket-noi')}>
                  <Button variant="primary">{t('troLyAiVanHanhThuNghiem.sangKetNoi')}</Button>
                </Link>
              }
            />
          </div>
        ) : null}

        <TabsTruot
          items={[
            { key: 'sql', label: t('troLyAiVanHanhThuNghiem.tabCauLenh'), children: theCauLenh },
            { key: 'context', label: t('troLyAiVanHanhThuNghiem.tabNguCanh'), children: theNguCanh },
          ]}
        />
      </div>
    </div>
  );
}
